import React from 'react';
import LmsRolePage from './LmsRolePage';

const relatedLinks = [
  { label: 'LMS for Airlines', path: '/lms-airlines' },
  { label: 'LMS for Aviation Companies', path: '/lms-aviation-companies' },
];

const LmsAnspCaaPage: React.FC = () => {
  return (
    <LmsRolePage
      title="AviaGov LMS for ANSPs and CAAs"
      seoTitle="AviaGov LMS for ANSPs and CAAs | ATCO Training and Qualification Management"
      description="Governed LMS for ANSP and CAA environments with ATCO objective management, qualification records, recurrence controls, audit-ready evidence, and authority oversight support."
      eyebrow="ANSPs and CAAs"
      heroNote="Built for ATCO environments, authority oversight, and governed qualification records."
      heroImagePrompt="A structured aviation authority and ANSP training governance scene with competency records, ATCO objective mapping, oversight workflows, and evidence folders on a dark institutional backdrop, minimal UI, formal and precise"
      heroImageAlt="ANSP and CAA training governance view showing competency records, ATCO objective structures, and oversight workflows"
      painPoints={[
        'Air navigation service provider training environments often rely on disconnected tools for air traffic control training, objective coverage, and evidence review.',
        'ATCO objective coverage is often managed outside the LMS in spreadsheets, policy packs, and disconnected evidence trails.',
        'Authorities need traceable qualification evidence, not only course attendance and completion percentages.',
        'Recurrence, assessor authority, and verification status are difficult to defend during audits when records live across multiple systems.',
        'Training actions triggered by safety findings are hard to connect back to oversight and compliance review.',
      ]}
      capabilities={[
        'Air traffic control training and ATCO training records can sit inside one governed model instead of fragmented spreadsheets and isolated course histories.',
        'ATCO framework packs, objective catalogs, and objective mapping into lessons, assessments, and live-session activity.',
        'Objective evidence flows with pass rules, assessor verification, learner qualification visibility, and attendance-backed evidence writing from live delivery.',
        'Qualification programs, recurrence policies, qualification records, and assessor authority controls in one governed workspace.',
        'Compliance-pack activation, governance export profiles, and starter overlays for framework-led deployments.',
        'Shared framework support for ATCO, ATSEP, and PANS-OPS-oriented training structures on the same governed stack.',
        'Analytics queues for at-risk learners, compliance exceptions, and learner-level evidence review.',
        'Optional portal delivery for controlled external learning without losing institutional governance.',
      ]}
      workflow={[
        {
          title: 'Define competency structure',
          description: 'Start with training governance, framework packs, and ATCO-specific overlays so the organization defines qualification programs, recurrence windows, objective frameworks, and who is authorized to verify outcomes.',
          items: [
            'Qualification programs and recurrence policies',
            'ATCO, ATSEP, and PANS-OPS-oriented framework packs and objective catalogs',
            'Assessor authority registers and evidence rules',
          ],
        },
        {
          title: 'Run governed training delivery',
          description: 'Deliver internal or controlled portal training through courses, cohorts, lessons, assessments, and live sessions while preserving direct linkage to governed records, objective evidence, and segment-aware batch delivery.',
          items: [
            'Cohorts, segment scheduling, batch-run controls, and live calendars',
            'Objective-linked assessments, pass rules, and evidence-aware evaluation',
            'Certificates, portal qualification views, and learner history with audit-ready traceability',
          ],
        },
        {
          title: 'Support oversight and review',
          description: 'Use qualification records, attention queues, compliance exceptions, governance export profiles, and compliance-pack outputs to support internal governance, training organization oversight, and authority review without relying on ad hoc spreadsheets.',
          items: [
            'Learner-level qualification visibility',
            'Compliance exception and qualification-attention monitoring',
            'Structured exports, active pack overlays, and record retention support',
          ],
        },
      ]}
      outcomes={[
        'One governed record model connecting ATCO training delivery, evidence capture, recurrence, and authority verification.',
        'Reduced audit friction because objective coverage, assessor authority, and qualification status remain traceable end to end.',
        'A clearer bridge between safety-driven training actions and the evidence expected in ANSP and CAA oversight.',
      ]}
      keywords={[
        'LMS for ANSP',
        'LMS for CAA',
        'aviation authority LMS',
        'air traffic control training',
        'ATCO training LMS',
        'ATCO training',
        'air navigation service provider training',
        'training organization oversight',
        'ATCO qualification management',
        'ANSP training management',
      ]}
      faq={[
        {
          question: 'Can this LMS support ATCO objective management?',
          answer: 'Yes. AviaGov LMS includes ATCO framework packs, objective catalogs, mappings into lessons and assessments, and evidence-aware evaluation connected to governed qualification records.',
        },
        {
          question: 'Is AviaGov LMS suitable for authority oversight workflows?',
          answer: 'Yes. The platform supports qualification programs, recurrence policies, assessor authority controls, compliance exceptions, and export-ready training records for oversight-oriented deployments.',
        },
        {
          question: 'Can ANSPs and CAAs connect training with safety findings?',
          answer: 'Yes. AviaGov positions training as part of a governed evidence model, so safety-driven training actions can be tracked alongside qualification and oversight review.',
        },
      ]}
      relatedLinks={relatedLinks}
      ctaId="lms_ansp_caa_walkthrough"
    />
  );
};

export default LmsAnspCaaPage;
